import React from 'react';
import './Skeleton.scss';

export type SkeletonProps = typeof Skeleton.defaultProps & {
  page?: 'reviews' | 'bag' | 'empty' | 'header' | 'pdp';
};

/**
 * The Skeleton component is used at the page level to show the user a rough outline of the current page while a page is fetching data.
 *
 * However, since Skeleton is a React component, Skeleton itself can't render until React is ready. To get around this, we hard-code the identical HTML and CSS rendered by Skeleton into the appropriate [Raven nest template](https://github.com/express-labs/raven-nest/tree/develop/v1.x/templates/express.com).
 *
 * ### Steps for Copying the Skeleton Component HTML and CSS into to a Raven Nest Page Template:
 * - Pick an appropriate Story Book example below.
 * - Open dev tools in your browser.
 * - Copy the HTML paste it directly in your server-side [page template](https://github.com/express-labs/raven-nest/tree/develop/v1.x/templates/express.com) inside a div with that contains the className "isLoading"
 * - Then copy the CSS listed below and put it into the `<head></head>` tag of your chosen [page template](https://github.com/express-labs/raven-nest/tree/develop/v1.x/templates/express.com)
 *
 * Use JavaScript to delete the div containing a class of "isLoading" once React page content is ready.
 *
 * ### Copy This CSS
 * ```css
 * .skel{display:flex;flex-direction:row;flex-wrap:wrap;max-width:1348px;margin:auto}.skel__cell-1{flex-basis:100%;flex-grow:0;flex-shrink:1;order:1}@media all and (min-width: 992px){.skel__cell-1{flex-basis:66.02374%}}.skel__cell-2{flex-grow:0;flex-shrink:1;flex-basis:100%;order:3}@media all and (min-width: 992px){.skel__cell-2{flex-basis:33.97626%;order:2}}.skel__cell-3{display:none;flex-grow:0;flex-shrink:1;order:2}@media all and (min-width: 380px){.skel__cell-3{display:block;flex-basis:100%}}@media all and (min-width: 992px){.skel__cell-3{flex-basis:66.02374%;order:3}}@media all and (min-width: 992px){.skel__carousel{overflow:hidden;margin:14px}}.skel__carousel__thumbs{display:none}@media all and (min-width: 577px){.skel__carousel__thumbs{display:block;float:left;width:7.07657%;max-width:61px}}.skel__carousel__thumb{max-width:58px;height:0;margin-right:3px;margin-bottom:3px;padding-bottom:120.68966%;background-color:#f4f4f4}.skel__carousel__image{width:77.66667%;height:0;margin:0 auto;padding-bottom:97.33333%;background-color:#f4f4f4}@media all and (min-width: 577px){.skel__carousel__image{float:left;width:92.92343%;height:0;margin:0;padding-bottom:58.81671%}}.skel__heading{display:none;margin:14px 0 16px;background-color:#f4f4f4}@media all and (min-width: 380px){.skel__heading{display:block;max-width:430px;height:36px;margin:14px 0 16px}}@media all and (min-width: 992px){.skel__heading{max-width:auto;height:60px;margin:14px 14px 16px}}.skel__reviews{display:none;max-width:140px;height:14px;margin:14px 0 17px;background-color:#f4f4f4}@media all and (min-width: 380px){.skel__reviews{display:block;margin:14px 0 17px}}@media all and (min-width: 992px){.skel__reviews{margin:14px 14px 17px}}.skel__price{max-width:62px;height:22px;margin:36px 0 8px auto;background-color:#f4f4f4}@media all and (min-width: 480px){.skel__price{margin:14px 0 8px}}@media all and (min-width: 992px){.skel__price{margin:14px 14px 8px}}.skel__deal{max-width:123px;height:15px;margin:0 0 14px auto;background-color:#f4f4f4}@media all and (min-width: 480px){.skel__deal{margin:14px 0 8px}}@media all and (min-width: 992px){.skel__deal{margin:14px 14px 8px}}.skel__description{height:100px;background-color:#f4f4f4}@media all and (min-width: 992px){.skel__description{height:80px;margin:14px}}@media all and (min-width: 992px){.skel__color-name{max-width:70px;height:14px;margin:14px 14px 4px;background-color:#f4f4f4}.skel__color-swatches{margin-left:6px}.skel__color-swatch{display:inline-block;width:25px;height:25px;margin:3px 8px;border-radius:50%;background-color:#f4f4f4}.skel__sizechart__link{width:60px;height:16px;margin:14px 14px 16px;background-color:#f4f4f4}.skel__sizechart__buttons{height:38px;margin:14px 14px 16px;border-radius:4px;background-color:#f4f4f4}}.skel__also{max-width:74px;height:24px;margin:36px 0 16px;background-color:#f4f4f4}@media all and (min-width: 992px){.skel__also{margin:14px 14px 16px}}.skel__also__thumbs{overflow:hidden;margin:14px 0}@media all and (min-width: 992px){.skel__also__thumbs{margin:14px}}.skel__also__thumb{float:left;width:15.73034%;height:0;margin:0 .89888% 0 0;padding-bottom:23.59551%;background-color:#f4f4f4}
 *
 * ```
 * Note: The PDP, Bag, and Default skeletons are all exactly the same as of 7/20/2023.
 */
export default class Skeleton extends React.Component<SkeletonProps> {
  public static displayName = 'Skeleton';
  static defaultProps = {
    showSpinner: true,
  };

  renderBag() {
    return (
      <div id="skel" className="skel isLoading" data-skel-template="bag">
        <div className="skel__cell-1">
          <div className="skel__carousel">
            <div className="skel__carousel__thumbs">
              <div className="skel__carousel__thumb" />
              <div className="skel__carousel__thumb" />
            </div>
            <div className="skel__carousel__image" />
          </div>
        </div>
        <div className="skel__cell-2">
          <div className="skel__heading" />
          <div className="skel__reviews" />
          <div className="skel__price" />
          <div className="skel__deal" />
          <div className="skel__description" />
          <div className="skel__color-name" />
          <div className="skel__color-swatches">
            <span className="skel__color-swatch" />
            <span className="skel__color-swatch" />
            <span className="skel__color-swatch" />
          </div>
          <div className="skel__sizechart__link" />
          <div className="skel__sizechart__buttons" />
        </div>
        <div className="skel__cell-3">
          <div className="skel__also" />
          <div className="skel__also__thumbs">
            <div className="skel__also__thumb" />
            <div className="skel__also__thumb" />
            <div className="skel__also__thumb" />
            <div className="skel__also__thumb" />
            <div className="skel__also__thumb" />
            <div className="skel__also__thumb" />
          </div>
        </div>
        {this.props.showSpinner && (
          <div className="skel__overlay">
            <div className="skel__spinner" />
          </div>
        )}
      </div>
    );
  }

  renderEmpty() {
    return (
      <div id="skel" className="skel isLoading" data-skel-template="empty">
        <div className="skel_empty__container" />
        {this.props.showSpinner && (
          <div className="skel__overlay">
            <div className="skel__spinner" />
          </div>
        )}
      </div>
    );
  }

  renderPdp() {
    return (
      <div id="skel" className="skel isLoading" data-skel-template="pdp">
        <div className="skel__cell-1">
          <div className="skel__carousel">
            <div className="skel__carousel__thumbs">
              <div className="skel__carousel__thumb" />
              <div className="skel__carousel__thumb" />
            </div>
            <div className="skel__carousel__image" />
          </div>
        </div>
        <div className="skel__cell-2">
          <div className="skel__heading" />
          <div className="skel__reviews" />
          <div className="skel__price" />
          <div className="skel__deal" />
          <div className="skel__description" />
          <div className="skel__color-name" />
          <div className="skel__color-swatches">
            <span className="skel__color-swatch" />
            <span className="skel__color-swatch" />
            <span className="skel__color-swatch" />
          </div>
          <div className="skel__sizechart__link" />
          <div className="skel__sizechart__buttons" />
        </div>
        <div className="skel__cell-3">
          <div className="skel__also" />
          <div className="skel__also__thumbs">
            <div className="skel__also__thumb" />
            <div className="skel__also__thumb" />
            <div className="skel__also__thumb" />
            <div className="skel__also__thumb" />
            <div className="skel__also__thumb" />
            <div className="skel__also__thumb" />
          </div>
        </div>
        {this.props.showSpinner && (
          <div className="skel__overlay">
            <div className="skel__spinner" />
          </div>
        )}
      </div>
    );
  }

  renderReviews() {
    return (
      <div id="skel" className="skel isLoading" data-skel-template="reviews">
        {this.props.showSpinner && (
          <div className="skel__overlay">
            <div className="skel__spinner" />
          </div>
        )}
      </div>
    );
  }

  renderHeader() {
    return (
      <div
        id="skel"
        className="skel_full isLoading"
        data-skel-template="header"
      >
        <div className="skel__header">
          <div className="skel__banner"></div>
          <div className="skel__logos"></div>
          <div className="skel__menu"></div>
        </div>
        {this.props.showSpinner && (
          <div className="skel__overlay">
            <div className="skel__spinner" />
          </div>
        )}
      </div>
    );
  }

  renderSkeleton() {
    const { page } = this.props;
    switch (page) {
      case 'reviews':
        return this.renderReviews();
      case 'bag':
        return this.renderBag();
      case 'empty':
        return this.renderEmpty();
      case 'header':
        return this.renderHeader();
      default:
        return this.renderPdp();
    }
  }

  render() {
    return (
      <div
        id="skel-code-wrapper"
        data-note="this is not part of the skel, it's just a wrapper. The stuff INSIDE this div is the skeleton."
      >
        {this.renderSkeleton()}
      </div>
    );
  }
}
